import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import { Checkbox } from '../pilot-ui/checkbox';
import { Input } from '../pilot-ui/input';
import { Textarea } from '../pilot-ui/textarea';
import { Link } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { useWorkspaceDevice } from './workspaceContext';
import { useDeviceProjectionFollow } from '../../hooks/useDeviceProjectionFollow';
import { own } from '../../utils/ownKey';
import EditEquipmentDrawer from './EditEquipmentDrawer';
import { EDIT_EQUIPMENT_PERMISSION, EquipmentTargetKind } from '../../api/equipmentEdit';
import {
  missingFieldLabel,
  routeLabel,
  routeReasonLabel,
  stateLabel,
} from '../../utils/equipmentQuality';
import { EASTERN_ZONE_LABEL, easternDate, easternDateTime } from '../../utils/easternTime';
import {
  ALLOW_ANSWERS_THAT_RELOAD,
  BODY_MAX,
  Blocker,
  CONTACT_USERS_PERMISSION,
  ComponentFact,
  ComponentName,
  ComponentNote,
  DraftGate,
  DraftResponse,
  EMAIL_OFF_TEXT,
  EMAIL_PREFERENCE_UNAVAILABLE_TEXT,
  EmailPreferenceState,
  EquipmentNeedsResponse,
  NeedsComponent,
  NeedsRecipient,
  ReplyOutcome,
  RequestHistoryRow,
  RequestRefusal,
  RequestResultResponse,
  RecipientVerification,
  RequestTarget,
  SUBJECT_MAX,
  SendRequestBody,
  blockerCode,
  blockerKey,
  blockerSentence,
  draftRefusal,
  fetchEquipmentNeeds,
  fetchRequestResult,
  isAmbiguousSendOutcome,
  newRequestId,
  noteSentence,
  recordRequestReply,
  renderEquipmentDraft,
  refusalSentence,
  replyRefusal,
  requestRefusal,
  resubscribeAddress,
  resubscribeRefusal,
  sendEquipmentRequest,
  sendVerificationEmail,
  unsubscribeWhy,
  verificationEmailRefusal,
  verificationEmailSentSentence,
} from '../../api/equipmentNeeds';

/**
 * Plan 123 — the Missing tab.
 *
 * What is still unknown about this home's system, and the one place an
 * operator can do something about it: tick the components a photo would
 * settle, read the draft the renderer wrote, and send it to the homeowner.
 *
 * Five blocks, top to bottom (§6): the components, who the message would go
 * to, the draft, Send, and every request this system has ever had.
 *
 * Three things this screen is careful about.
 *
 * THE CLAIM MARKER travels from the draft to the send untouched. The console
 * composes no address, no owner and no sibling list — it echoes back the
 * `system_key`, `claim_id` and `observed_at` of the draft it is showing, and
 * the server refuses the send if any of them moved. That is what stops a
 * message written for one household going to the next one.
 *
 * THE REQUEST ID is minted when Send is clicked and reused for every attempt
 * at that request. A send that does not answer is resolved by READING it
 * back by its id, never by sending again — the message may well have gone
 * out.
 *
 * TIMES ARE EASTERN (§11), named once at the top rather than on every line.
 *
 * R11 — THE OPERATOR DRIVES (Chris, 2026-09-22). Every part of the system is
 * tickable, in every state, however recently it was asked for and whatever
 * the homeowner said. What the screen knows about a part is told as a NOTE —
 * grey, in words, disabling nothing — and only a reason the server would
 * REFUSE the send turns Send off. "I want the admin to be able to drive the
 * requests."
 */

const COMPONENT_LABEL: Record<ComponentName, string> = {
  outdoor_unit: 'Outdoor unit',
  indoor_unit: 'Indoor unit',
  // FS-838: listed only while the coil is the system's open tonnage ask.
  indoor_coil: 'Indoor coil',
  package_unit: 'Packaged unit',
  filter: 'Filter',
};

/**
 * The component's heading. A component this console does not know yet is
 * shown by its code rather than crashing the tab: the server can present a
 * new one before the console that names it ships.
 */
const componentLabel = (component: ComponentName): string =>
  own(COMPONENT_LABEL, component) ?? String(component);

/** The component in a sentence: "a photo of the outdoor unit"; "this part" for one it does not know. */
const componentPhrase = (component: ComponentName): string =>
  own(COMPONENT_LABEL, component)?.toLowerCase() ?? 'this part';

/**
 * Plan 123 Phase 4b — the six words this screen uses for where a part stands.
 *
 * Every other screen that talks about the same rows uses the same six, so a
 * part is never "Blocked" here and "Operations follow-up" on the Equipment
 * Inventory. `blocked` is not in the map: what it says depends on who has to
 * act, and that comes off the reason (`BLOCKED_REASONS` below).
 */
const STATE_LABEL: Record<string, string> = {
  documented: 'Documented',
  needed: 'Needs a photo',
  requested: 'Asked',
  not_applicable: 'Not on this system',
};

/** The two chips a blocked part can wear, and nothing else. */
export const WAITING_ON_US = 'Waiting on us';
export const NEEDS_A_LOOK = 'Needs a look';

const STATE_CLASS: Record<string, string> = {
  documented: 'border border-status-success-border bg-status-success text-status-success-foreground',
  needed: 'border border-status-warning-border bg-status-warning text-status-warning-foreground',
  requested: 'border border-status-info-border bg-status-info text-status-info-foreground',
  not_applicable: 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground',
  blocked: 'bg-indigo-100 text-indigo-800',
};

/** "Needs a look" is a person's decision, not a queue, so it reads louder. */
const LOOK_CLASS = 'border border-status-error-border bg-status-error text-status-error-foreground';

const NEEDS_A_PHOTO = 'Needs a photo';

/**
 * A sentence-case label dropped into the middle of a line. `stateLabel`
 * writes "Missing" because it heads a column elsewhere; mid-sentence, after
 * "unknown · ", a capital reads as a second sentence starting.
 */
export const lowerFirst = (value: string): string =>
  value ? value.charAt(0).toLowerCase() + value.slice(1) : value;

/**
 * Plan 123 Phase 4b — why a part is held, as one sentence that says who acts.
 *
 * The screen used to print the route label and the route reason back to back
 * ("We can work it out · propose a catalog row from the label photo"), which
 * told an operator neither what was wrong nor whose turn it was. One sentence
 * per reason replaces both: it opens with the actor, then says the one thing
 * that would move the part on.
 *
 * `{model}` is filled from the inventory's evidence line when it names one,
 * and reads "the filter" when it does not.
 *
 * The keys are the route reasons device-data-api emits (`src/equipment/
 * inventory.ts`). A reason with no entry still gets a sentence, and the
 * coverage test treats that as a gap.
 */
export const BLOCKED_REASONS: Record<string, { chip: string; sentence: string }> = {
  // --- ours to do -------------------------------------------------------
  catalog_proposal_from_photo: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the label photo names {model} but the filter catalog has no row for it. Propose the catalog row from the photo, then the curve is known.',
  },
  ac_model_decode: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the A/C model number is on file and still has to be decoded to a tonnage.',
  },
  hvac_model_decode: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the furnace or air handler model number is on file and still has to be decoded.',
  },
  // R7 removed `ac_photo_on_file`, `hvac_photo_on_file` and
  // `filter_photo_on_file` from here. All three said a photo "has not been
  // read yet", and nothing was ever going to read it: the app analyzes a
  // label at upload and no back-end pass revisits a stored one. A photo
  // beside a fact that is still missing was read and gave nothing, which the
  // server now routes to the homeowner as `photo_unreadable`.
  legacy_value_migration: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: an old value is on file in a form this product no longer reads, and it still has to be migrated.',
  },
  catalog_match_work: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the filter is known and still has to be matched to a catalog curve.',
  },
  catalog_has_no_rated_esp: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the manufacturer document for this model gives no rated pressure. We have to find one, or record that there is none.',
  },
  // FS-752 — the furnace or air handler record: the model number is on file
  // and the catalog work behind it is ours.
  catalog_document_missing: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the furnace or air handler model is known, but we have no manufacturer document for it yet. Find the manual or spec sheet and add it to the catalog.',
  },
  catalog_type_unconfirmed: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the furnace or air handler model is known, but the catalog does not say what kind of unit it is. Confirm the equipment type in the catalog.',
  },
  catalog_no_source: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: the furnace or air handler model is real, but no manufacturer information has been found for it yet. Keep looking, or record that there is none.',
  },
  // FS-786: the research found and stored the document and kept no fact.
  catalog_document_no_facts: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: we have the manufacturer document for the furnace or air handler model, and its facts still need reading. This is on us, not the homeowner.',
  },
  model_not_in_catalog: {
    chip: WAITING_ON_US,
    sentence:
      'Waiting on us: this model is not in our equipment catalog yet. Add it, and the rated pressure follows.',
  },

  // --- a person has to decide or look -----------------------------------
  // R3 F27: the server raises this at MORE THAN TWO DAYS old, so it is not a
  // queue to wait in — somebody has to look. R4 F32: what the age establishes
  // is that nothing has refreshed the summary, not what tonight will do with
  // it, so the sentence stops there.
  projection_stale: {
    chip: NEEDS_A_LOOK,
    sentence:
      "Needs a look: this home's summary is more than two days old and nothing has refreshed it.",
  },
  user_vs_catalog: {
    chip: NEEDS_A_LOOK,
    sentence:
      "Needs a decision: the homeowner's answer and the catalog disagree. Pick one in Enter by hand.",
  },
  // FS-752 — the furnace or air handler record needs a person to decide.
  model_ambiguous: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: the furnace or air handler model number matches more than one product. Pick the right one in the catalog.',
  },
  model_wrong_component: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: the model on the furnace record belongs to another unit, such as the outdoor unit or the coil. Move it to the right record.',
  },
  photo_unclassified: {
    chip: NEEDS_A_LOOK,
    sentence: 'Needs a look: a photo is on file that nobody has placed on a part yet.',
  },
  shared_record_multiple_filters: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: two filter sizes are recorded on one system. Sort out which Clip has which filter first.',
  },
  applicability_conflict: {
    chip: NEEDS_A_LOOK,
    sentence: 'Needs a look: this part is declared absent, but the record has evidence of it.',
  },
  recapture_or_confirm: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: a value is on file that nothing can rank. Ask for a new photo, or confirm the figure with the homeowner.',
  },
  motor_decode_unconfirmed: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: the motor type came from the model number alone and nothing confirms it. A photo of the furnace label would.',
  },
  no_evidence: {
    chip: NEEDS_A_LOOK,
    sentence: 'Needs a look: nothing on file names this part, and no photo request will settle it.',
  },
  no_owner_email: {
    chip: NEEDS_A_LOOK,
    sentence: 'Needs a look: there is nobody to write to, because this home has no email address on file.',
  },
  // The three holds the legacy guard records, each a different piece of work.
  legacy_guard_pending: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: a write from an old app build has not been processed yet, so this record is held.',
  },
  legacy_identity_held: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: an identity that belongs to another part was moved out of this record and kept for review.',
  },
  typed_coil_model_on_parent: {
    chip: NEEDS_A_LOOK,
    sentence:
      'Needs a look: the indoor unit carries a model number the catalog reads as an evaporator coil.',
  },

  // --- nobody is registered to the Clip ---------------------------------
  claim_missing: {
    chip: NEEDS_A_LOOK,
    sentence:
      'No app account is registered to this Clip, so nothing about this system can be trusted yet.',
  },
  claim_ambiguous: {
    chip: NEEDS_A_LOOK,
    sentence:
      'More than one app account is registered to this Clip, so nothing about this system can be trusted yet. Settle which one is current.',
  },
  claim_stale: {
    chip: NEEDS_A_LOOK,
    sentence:
      'The app account registered to this Clip is out of date, so nothing about this system can be trusted yet.',
  },
  claim_not_current: {
    chip: NEEDS_A_LOOK,
    sentence:
      'The app account registered to this Clip is not the current one, so nothing about this system can be trusted yet.',
  },

  // --- the homeowner, on a part that cannot be ticked right now ---------
  ask_outdoor_photo: {
    chip: NEEDS_A_PHOTO,
    sentence: 'Waiting on the homeowner: a photo of the outdoor unit would settle this.',
  },
  ask_hvac_photo: {
    chip: NEEDS_A_PHOTO,
    sentence: 'Waiting on the homeowner: a photo of the furnace or air handler would settle this.',
  },
  ask_filter_photo: {
    chip: NEEDS_A_PHOTO,
    sentence: 'Waiting on the homeowner: a photo of the filter label would settle this.',
  },
  // FS-838: a furnace system's size, asked of the coil before the outdoor unit.
  ask_coil_photo: {
    chip: NEEDS_A_PHOTO,
    sentence:
      'Waiting on the homeowner: a photo of the indoor coil label would settle this. The '
      + 'furnace\'s own label gives only an "up to" size, so the coil comes next, then the outdoor unit.',
  },
};

/**
 * The filter model out of the inventory's own evidence line, which the API
 * composes as `identity from a label photo; model HCF16-11; filter photos 1`.
 * A fact carries no separate model field, and "propose a catalog row" without
 * saying for what is the sentence Chris could not read.
 */
const MODEL_IN_EVIDENCE = /(?:^|;\s*)model\s+([^;]+)/i;

export const modelFromEvidence = (evidence: Array<string | null>): string | null => {
  for (const line of evidence) {
    const found = line ? MODEL_IN_EVIDENCE.exec(line) : null;
    const model = found?.[1]?.trim();
    if (model) return model;
  }
  return null;
};

/**
 * Plan 123 R7 — the equipment model number out of the inventory's evidence
 * line, which names the side it came off: `hvac model 00; catalog says not a
 * model; outdoor photos 0`.
 *
 * `MODEL_IN_EVIDENCE` above cannot read those: it anchors `model` to the
 * start of a segment, which is the filter's shape and not this one. Quoting
 * the number back is the difference between a sentence Chris can check
 * against the record and one he has to go and look up.
 */
const MODEL_NUMBER_IN_EVIDENCE = /(?:^|;\s*)(?:(?:hvac|ac)\s+)?model\s+([^;]+)/i;

export const modelNumberFromEvidence = (evidence: Array<string | null>): string | null => {
  for (const line of evidence) {
    const found = line ? MODEL_NUMBER_IN_EVIDENCE.exec(line) : null;
    const model = found?.[1]?.trim();
    if (model) return model;
  }
  return null;
};

/**
 * Plan 123 R7 — why a NEEDED part is waiting on the homeowner, in one
 * sentence.
 *
 * Chris read device 266 and could not tell what the tab wanted: an outdoor
 * unit with nothing on file he could see, an air-handler photo that turned
 * out to be a warning label, and a filter brand the homeowner had typed as
 * "no clue". The state chip said "Needs a photo" and nothing said WHY, so
 * every row looked the same as every other one.
 *
 * Same register as `BLOCKED_REASONS`: open with who acts, then the one thing
 * that would move the part on. `{model}` is the model number off the evidence
 * line, in parentheses, and drops out entirely when the line names none —
 * "the model number on file ()" would be worse than no number at all.
 *
 * The keys are the route reasons the server puts on a NUDGE fact. A reason
 * with no entry here writes nothing: a needed row is not held, so it has
 * nothing to apologize for, and a line about a missing sentence would be one
 * more thing on screen that is about us rather than about the system.
 */
export const NEEDED_REASONS: Record<string, string> = {
  // --- the back end has had its go and came away with nothing -----------
  model_not_a_model:
    'Waiting on the homeowner: the model number on file{model} is not a model number, so '
    + 'nothing can be decoded from it. A photo of the label would settle this.',
  model_decode_exhausted:
    'Waiting on the homeowner: the model number on file was looked up and does not settle '
    + 'this. A photo of the label would.',
  photo_unreadable:
    'Waiting on the homeowner: a photo is on file, but nothing usable could be read from it. '
    + 'Ask for a clearer photo of the label, or read the photo yourself and use Enter by hand.',

  // --- the asks that were already there, said the same way as when the
  //     part is blocked on one ------------------------------------------
  ask_outdoor_photo: BLOCKED_REASONS.ask_outdoor_photo.sentence,
  ask_hvac_photo: BLOCKED_REASONS.ask_hvac_photo.sentence,
  ask_filter_photo: BLOCKED_REASONS.ask_filter_photo.sentence,
  ask_coil_photo: BLOCKED_REASONS.ask_coil_photo.sentence,
  recapture_or_confirm: BLOCKED_REASONS.recapture_or_confirm.sentence,
  motor_decode_unconfirmed: BLOCKED_REASONS.motor_decode_unconfirmed.sentence,
};

/** What a needed part is waiting for, or null when there are no words. */
export const neededSentence = (
  reason: string | null | undefined,
  model?: string | null,
): string | null => {
  const sentence = own(NEEDED_REASONS, reason);
  if (!sentence) return null;
  return sentence.replace('{model}', model ? ` (${model})` : '');
};

/** The chip a blocked part wears. Unknown reasons read as a person's problem. */
export const blockedChip = (reason: string | null | undefined): string =>
  own(BLOCKED_REASONS, reason)?.chip || NEEDS_A_LOOK;

/** Why a part is held, in one sentence. */
export const blockedSentence = (
  reason: string | null | undefined,
  model?: string | null,
): string => {
  const entry = own(BLOCKED_REASONS, reason);
  if (!entry) {
    return `Needs a look: this part is held for a reason this screen has no words for${
      reason ? ` (${reason})` : ''
    }.`;
  }
  return entry.sentence.replace('{model}', model ? `model ${model}` : 'the filter');
};

/**
 * The one thing left to say under a photo request box: the account.
 *
 * R7 wrote a sentence under every shut box explaining why it was shut — this
 * part is documented, this one needs a look, this one was asked for already.
 * R11 has no shut boxes. The operator decides what to ask for, so the state
 * of a part is a NOTE next to it rather than a reason a control is off, and
 * the only thing that closes every box on the tab is an account with no
 * permission to write to homeowners.
 */
export const photoRequestHelp = (canContact: boolean): string | null =>
  (canContact ? null : 'Your account cannot write to homeowners.');

/**
 * Plan 123 R11 — the mouseover text for every control on this tab.
 *
 * Chris, 2026-09-22: "ensure that every control in these UI elements has a
 * mouseover help text that is very useful for knowing what the control
 * does." The reader is an operator who has never used the feature, so each
 * one says what the control DOES and what happens next — not what it is
 * called. They live in one map so a test can walk every control on screen
 * and prove none of them is silent.
 */
export const TOOLTIPS = {
  requestPhoto: (part: string) =>
    `Include the ${part} in the email. Tick as many parts as you like; the draft below lists `
    + 'each one. You can ask for a part that is already on file or was asked for before.',
  notOnSystem: (part: string) =>
    `Record that this home has no ${part}. It stops counting against the equipment score and `
    + 'the draft stops asking for it.',
  enterByHand: (fact: string) =>
    `Open the equipment editor and type the ${fact} yourself, for when you already know it.`,
  resetDraft: 'Throw away your edits and restore the drafted subject and message.',
  subject: "The email's subject. Edit freely; the homeowner sees exactly this.",
  body:
    "The email's body. Edit freely; the homeowner sees exactly this. FluxHive adds the layout, "
    + 'the footer and the unsubscribe line.',
  send: (address: string) =>
    `Email this message to ${address} now. One click sends; there is no confirmation step.`,
  sendUnavailable: (reason: string) => `Send is unavailable: ${reason}`,
  sendNothingTicked: 'Tick a part first. Send needs at least one part of the system on the list.',
  sendDraftPending: 'The draft is still being written. Send turns on as soon as it lands.',
  sendInFlight: 'The message is being sent. Nothing else can be sent until this one answers.',
  checkStatus: 'Read this request back from the server. It never sends anything.',
  cancelRecovery:
    'Stop tracking this attempt here. Nothing is sent or unsent; the history below still shows it.',
  replyPhotoReceived: (part: string) =>
    `Record that the homeowner sent the ${part} photo. Then upload it through the equipment `
    + 'editor so the record updates.',
  replyNotOnSystem: (part: string) =>
    `Record the homeowner's answer that this home has no ${part}. You can then declare the `
    + 'absence so the score stops counting it.',
  replyDeclined: 'Record that the homeowner declined. You can ask again any time.',
  absenceOffer:
    'Open the equipment editor with the absence pre-selected. The part stops counting against '
    + 'the score.',
  retryLoad: 'Reload what is missing about this system.',
  retryDraft: 'Ask for the draft again. It writes a message; it sends nothing.',
  openLink: (thing: string) => `Open ${thing}.`,
  // FS-1021 (wording approved by Chris, 2026-10-16).
  resubscribe:
    'Use only when the owner has asked to get HVAC system emails again. Verification emails are separate.',
  resubscribeBusy: 'Allowing HVAC system emails again. This takes a moment.',
  // FS-789: the first two sentences are the ticket's; the address status is
  // there so an unsubscribed or internal address is seen before the click.
  sendVerification: (status: string) =>
    'Asks Google to send this owner the standard MyFluxHive verification link. '
    + `It doesn't count against the two automatic reminders. Address status: ${status}`,
  sendVerificationNotNeeded: (why: string) => `Not needed: ${why}`,
  sendVerificationBusy: 'Asking Google to send the verification email. This takes a moment.',
};

/**
 * Plan 123 R13 — who stopped the emails, said for a person: the link in the
 * email, or whoever else recorded the judgement.
 */
export const unsubscribedVia = (judgedBy: string | null | undefined): string =>
  (judgedBy === 'unsubscribe-link' ? 'the link in the email' : judgedBy || 'an unknown source');

/** FS-1021 — the Allow button (wording approved by Chris, 2026-10-16). */
export const ALLOW_EMAILS_LABEL = 'Allow HVAC system emails again';

/** FS-1021 — the line under "Who this goes to" when the owner has turned emails off. */
export const EMAIL_OFF_LINE = 'HVAC system emails are off for this owner.';

/**
 * FS-1021 — the off line, with the day the unsubscribe link was last used, in
 * Eastern. The click is history only: the owner may have turned emails off in
 * the app instead, or on and off again since, so the date sits beside the
 * state and never stands in for it.
 */
export const emailOffLine = (linkUnsubscribedAt: string | null | undefined): string => {
  const used = easternDate(linkUnsubscribedAt, '');
  return used ? `${EMAIL_OFF_LINE} Unsubscribe link used ${used}.` : EMAIL_OFF_LINE;
};

/** The blocker each setting that holds Send stands for. `on` holds nothing. */
const PREFERENCE_BLOCKER: Record<string, Blocker> = {
  off: { code: 'email_off', message: EMAIL_OFF_TEXT },
  unavailable: { code: 'email_preference_unavailable', message: EMAIL_PREFERENCE_UNAVAILABLE_TEXT },
};

/**
 * FS-1021 (r2 F39) — the owner's setting holds Send by its STATE, not only by
 * whatever blockers came with it.
 *
 * device-data-api sends `email_off` beside an off needs block, and the
 * renderer sends it beside an off draft, so normally this adds nothing. It is
 * here for the cases where the two disagree for a moment: a needs block read
 * after the owner turned emails off, over a draft rendered while they were on;
 * or a draft that could not read the setting. Either one alone keeps Send off.
 * A code already on the list is not added twice.
 */
export const withPreferenceBlockers = (
  blockers: Blocker[],
  states: Array<EmailPreferenceState | null | undefined>,
): Blocker[] => {
  const out = [...blockers];
  states.forEach((state) => {
    const implied = own(PREFERENCE_BLOCKER, state);
    if (implied && !out.some((b) => blockerCode(b) === blockerCode(implied))) out.push(implied);
  });
  return out;
};

/** The one link this tab offers, named the way the tooltip wants it. */
const EQUIPMENT_INVENTORY = 'the Equipment Inventory';

/** Where Enter by hand lands: the record, the box the cursor goes in, and the fact said out loud. */
export interface HandEntry {
  tab: EquipmentTargetKind;
  field: string;
  /** The field said out loud, for the control's mouseover. */
  fact: string;
}

/** By fact, for the facts whose record does not depend on the component. */
const HAND_ENTRY_BY_FACT: Record<string, HandEntry> = {
  indoor_record: { tab: 'hvac', field: 'modelNumber', fact: 'model number' },
  motor_type: { tab: 'hvac', field: 'blowerTypeNameplate', fact: 'blower motor type' },
  filter_curve: { tab: 'filter', field: 'manufacturer', fact: 'filter make and size' },
  filter_record: { tab: 'filter', field: 'manufacturer', fact: 'filter make and size' },
};

/** A row that owns no fact (or one this console does not know) lands by component. */
const HAND_ENTRY_BY_COMPONENT: Record<string, HandEntry> = {
  outdoor_unit: { tab: 'ac', field: 'tonnage', fact: 'tonnage' },
  indoor_unit: { tab: 'hvac', field: 'blowerTypeNameplate', fact: 'blower motor type' },
  indoor_coil: { tab: 'hvac', field: 'tonnage', fact: 'tonnage' },
  package_unit: { tab: 'hvac', field: 'tonnage', fact: 'tonnage' },
  filter: { tab: 'filter', field: 'manufacturer', fact: 'filter make and size' },
};

/**
 * FS-838 — which record an operator lands on when they choose to fill a fact
 * in themselves, and which box the cursor goes in, picked by the FIRST fact
 * the row owns that is not known yet (its first fact when every one is).
 *
 * The tonnage goes where the server says it lives now: on the indoor record
 * when the indoor unit owns it (an air handler, or a type nobody recorded),
 * on the outdoor record when the outdoor unit does, and on the coil record
 * for a coil row when there is a coil record to write to. Without one, or
 * with no coil count known, the indoor record takes it: an operator's entry
 * there is firm and wins.
 */
export const handEntryFor = (
  row: { component: ComponentName; facts?: Array<Pick<ComponentFact, 'field' | 'known'>> | null },
  coilCount?: number | null,
): HandEntry => {
  const facts = row.facts ?? [];
  const field = (facts.find((fact) => !fact.known) ?? facts[0])?.field;
  if (field === 'tonnage') {
    if (row.component === 'outdoor_unit') return { tab: 'ac', field: 'tonnage', fact: 'tonnage' };
    if (row.component === 'indoor_coil' && (coilCount ?? 0) > 0) {
      return { tab: 'coil', field: 'tonnage', fact: 'tonnage' };
    }
    return { tab: 'hvac', field: 'tonnage', fact: 'tonnage' };
  }
  return own(HAND_ENTRY_BY_FACT, field)
    ?? own(HAND_ENTRY_BY_COMPONENT, row.component)
    ?? { tab: 'hvac', field: 'modelNumber', fact: 'equipment details' };
};

/**
 * Where a photo of this component is attached. A coil label goes on the HVAC
 * record and the photo review places it on the coil: a coil is never a photo
 * target of its own.
 */
const PHOTO_RECORD: Record<ComponentName, EquipmentTargetKind> = {
  outdoor_unit: 'ac',
  indoor_unit: 'hvac',
  indoor_coil: 'hvac',
  package_unit: 'hvac',
  filter: 'filter',
};

/** §4.1 — the only two components a system can be declared not to have. */
const ABSENCE_COMPONENTS: ComponentName[] = ['outdoor_unit', 'indoor_unit'];
const ABSENCE_EDIT = { tab: 'hvac' as EquipmentTargetKind, field: 'absentComponents' };

const POLL_ATTEMPTS = 6;
const POLL_MS = 2000;

/**
 * Plan 123 R2 F9 — which Clip the equipment drawer opens on.
 *
 * A filter belongs to one Clip, and from the subject's workspace an operator
 * can be looking at a sibling's filter row. Opening the drawer on the subject
 * would correct the wrong machine's record while the label on screen names
 * the other one, so the row carries its own Clip into the drawer state and
 * the drawer is given THAT device.
 */
interface DrawerTarget {
  device: { id: number; name: string };
  tab: EquipmentTargetKind;
  field?: string;
}

const wait = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

/** One tickable thing on the tab: a shared component, or one Clip's filter. */
interface TargetRow {
  key: string;
  component: ComponentName;
  /** The Clip the row belongs to — the subject for a shared component. */
  deviceId: number;
  /** That Clip's serial, which is how the equipment drawer reads a record. */
  deviceName: string;
  /** Set only on a filter row: the Clip whose filter is being asked about. */
  filterDeviceId: number | null;
  label: string;
  data: NeedsComponent;
}

const rowKey = (component: ComponentName, filterDeviceId: number | null): string =>
  filterDeviceId === null ? component : `${component}:${filterDeviceId}`;

const parseKey = (key: string): { component: ComponentName; filterDeviceId: number | null } => {
  const [component, id] = key.split(':');
  return {
    component: component as ComponentName,
    filterDeviceId: id === undefined ? null : Number(id),
  };
};

/** The ticked keys as the renderer wants them, in the order they were ticked. */
const targetsFromKeys = (
  keys: string[],
): { components: ComponentName[]; filterDeviceIds: number[] } => {
  const components: ComponentName[] = [];
  const filterDeviceIds: number[] = [];
  keys.forEach((key) => {
    const { component, filterDeviceId } = parseKey(key);
    if (!components.includes(component)) components.push(component);
    if (filterDeviceId !== null && !filterDeviceIds.includes(filterDeviceId)) {
      filterDeviceIds.push(filterDeviceId);
    }
  });
  return { components, filterDeviceIds };
};

const rowLabel = (component: ComponentName, filterDeviceId: number | null): string =>
  filterDeviceId === null
    ? componentLabel(component)
    : `${componentLabel(component)} on device ${filterDeviceId}`;

/** The row named without an article, for a sentence that supplies its own. */
const rowPart = (component: ComponentName, filterDeviceId: number | null): string =>
  (filterDeviceId === null
    ? componentPhrase(component)
    : `${componentPhrase(component)} on device ${filterDeviceId}`);

/** The row in a sentence, for a control's label. */
const rowPhrase = (component: ComponentName, filterDeviceId: number | null): string =>
  (own(COMPONENT_LABEL, component) ? `the ${rowPart(component, filterDeviceId)}` : rowPart(component, filterDeviceId));

/**
 * Two blockers that say the same thing about the same Clip say it once.
 *
 * R3 §3: a blocker can now name a device, and one cooldown per filter is two
 * different facts. Deduping on the code alone hid the second one.
 */
const dedupeBlockers = (blockers: Blocker[]): Blocker[] => {
  const seen = new Set<string>();
  return blockers.filter((blocker) => {
    const key = blockerKey(blocker);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Plan 123 R11 — the notes on one thing, as one shape and said once.
 *
 * Two shapes arrive. The inventory's leftover route reasons have always come
 * as bare strings and are translated the way every other route reason on
 * this console is; an R11 note arrives as the blocker shape, carrying its
 * code, its Clip and its dates.
 *
 * They are deduped on what they SAY rather than on the code alone: two
 * cooldowns on one system, one per filter, are two facts (R4 F31), and two
 * copies of the same one are one line.
 */
const asNote = (note: ComponentNote): Blocker => {
  if (typeof note !== 'string') return note;
  // FS-838: the two capacity notes arrive bare and have sentences of their own.
  if (BARE_NOTE_CODES.has(note)) return { code: note, message: '' };
  return { code: 'route_reason', message: routeReasonLabel(note) };
};

/** Bare-string notes worded by `NOTE_TEXT` rather than as a route reason. */
const BARE_NOTE_CODES = new Set(['capacity_from_indoor_unit', 'capacity_from_indoor_coil']);

const notesOf = (notes: ComponentNote[] | undefined | null): Blocker[] => {
  const seen = new Set<string>();
  return (notes ?? []).map(asNote).filter((note) => {
    const key = `${blockerKey(note)}|${note.message ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** One note, in grey, under whatever it is about. It disables nothing. */
const NoteLines: React.FC<{ notes: Blocker[] }> = ({ notes }) => (
  <>
    {notes.map((note) => (
      <p
        key={`${blockerKey(note)}|${note.message ?? ''}`}
        data-testid="note"
        className="mt-1 text-sm text-gray-600"
      >
        {noteSentence(note)}
      </p>
    ))}
  </>
);

/**
 * Plan 123 R4 F30 — what repeated reads of a request established, and what
 * each of those is worth saying.
 *
 * Three different facts, and the screen used to have one sentence for all
 * three. Six 404s mean nothing has been found. Six 200s on a row that is
 * still sending mean the attempt IS recorded and the ten seconds of polling
 * were shorter than the two-minute Lambda timeout. A read that failed means
 * nobody knows, which is not the same as nothing being there.
 *
 * All three end in the same place — the id is kept, the confirmation is gone,
 * and the only control left is another READ — because none of them says the
 * message did not go out.
 */
type RecoveryState = 'absent' | 'recorded' | 'unreadable';

const RECOVERY_TEXT: Record<RecoveryState, string> = {
  absent: 'Nothing came back and no record of this attempt was found yet.',
  recorded: 'This attempt is recorded and has not finished yet. Check again in a minute.',
  unreadable: 'The attempt could not be checked. Try Check status again.',
};

/** A 404 after a row has already been read does not unsay the row. */
const RECORDED_BUT_NOT_FOUND =
  'This attempt is recorded, and this read did not find it. Check again in a minute.';

const NOTHING_RECORDED = 'Still nothing recorded for this attempt.';

/** What the polling loop came back with, and the last row it actually saw. */
interface PollOutcome {
  state: 'terminal' | RecoveryState;
  /** Kept so the wording after a later Check status stays consistent with it. */
  row: RequestResultResponse | null;
}

/** A send that has reached a state nothing else is going to change. */
const isTerminalResult = (result: RequestResultResponse): boolean => {
  if (result.status === 'refused') return true;
  const state = result.send?.state;
  return state === 'accepted' || state === 'failed' || state === 'unknown';
};

/**
 * What became of a send, in the words an operator would use out loud.
 *
 * R3 F25: `unknown` is the state where the Lambda committed its row and then
 * could not tell what SES did with it. It may have gone out and it may have
 * failed before dispatch, and the sentence has to leave both open — "went
 * out" is a claim the row does not support.
 */
const OUTCOME_TEXT: Record<string, string> = {
  accepted: 'The message was accepted for delivery.',
  failed: 'The message was not sent: the mail service would not take it.',
  unknown:
    'The message may have gone out; nothing confirmed it. Check the history before asking again.',
};

/**
 * R3 F25: a replay is not a second send, and it is not proof of a first one
 * either. An accepted attempt was sent once; a FAILED one was tried once and
 * refused, and calling that "already sent" tells the operator the homeowner
 * has a message they never got.
 */
const outcomeSentence = (state: string, replayed?: boolean): string => {
  if (replayed && state === 'failed') {
    return 'This attempt was already tried and the mail service refused it.';
  }
  const base = own(OUTCOME_TEXT, state) ?? `The send came back as ${state}.`;
  if (!replayed) return base;
  return state === 'unknown'
    ? `This attempt was already made. ${base}`
    : `This request was already sent. ${base}`;
};

/** The state of a send, for the one-line history summary. */
const SEND_STATE_TEXT: Record<string, string> = {
  accepted: 'Accepted for delivery',
  failed: 'Not accepted by the mail service',
  unknown: 'No confirmation came back',
  sending: 'Being sent',
  gated: 'Held back: sending was paused',
};

/**
 * R3 F27 — the two send states that put a message in front of a homeowner.
 *
 * Every row in the history is an ATTEMPT: the table is written when the
 * request is accounted for, before SES is called, and a refused or gated row
 * never reached SES at all. Only these two are described as messages.
 */
const SENT_STATES = new Set(['accepted', 'unknown']);

const sendStateText = (state: string): string => own(SEND_STATE_TEXT, state) ?? state;

/** The SES event names, as a person reads them. */
const EVENT_TEXT: Record<string, string> = {
  Send: 'Sent',
  Delivery: 'Delivered',
  Bounce: 'Bounced',
  Complaint: 'Marked as spam',
  Open: 'Opened',
  Click: 'Link clicked',
  Reject: 'Rejected by the mail service',
  Rendering_Failure: 'Could not be written',
  DeliveryDelay: 'Delivery delayed',
};

const eventText = (type: string): string => own(EVENT_TEXT, type) ?? type;

/**
 * FS-863 — who asked, by the console account's sign-in email. An account
 * deleted since is said to be one, with its number for whoever needs to
 * trace it; a row with no account at all is an unknown user.
 */
export const askerOf = (row: Pick<RequestHistoryRow, 'requested_by' | 'requested_by_email'>): string => {
  const email = row.requested_by_email?.trim();
  if (email) return email;
  const id = row.requested_by?.trim();
  return id ? `a deleted account (#${id})` : 'an unknown user';
};

/** A reply outcome in the operator's words. */
const REPLY_LABEL: Record<string, string> = {
  photo_received: 'Photo received',
  not_applicable: 'Not on this system',
  declined: 'Declined',
};

/** The three replies an operator can record, with what each button means. */
const REPLY_BUTTONS: Array<{
  value: ReplyOutcome;
  label: string;
  describe: (phrase: string) => string;
  /** R11 — and what recording it actually does, for the mouseover. */
  title: (part: string) => string;
}> = [
  {
    value: 'photo_received',
    label: 'Photo received',
    describe: (phrase) => `Record a photo received for ${phrase}`,
    title: (part) => TOOLTIPS.replyPhotoReceived(part),
  },
  {
    value: 'not_applicable',
    label: 'Not on this system',
    describe: (phrase) => `Record that ${phrase} is not on this system`,
    title: (part) => TOOLTIPS.replyNotOnSystem(part),
  },
  {
    value: 'declined',
    label: 'Declined',
    describe: (phrase) => `Record that the homeowner declined for ${phrase}`,
    title: () => TOOLTIPS.replyDeclined,
  },
];

/** The address as the database records it, in a phrase rather than a code. */
const ADDRESS_STATUS_TEXT: Record<string, string> = {
  verified: 'Address confirmed',
  internal: 'Internal FluxHive address',
  undeliverable: 'Address bounced before',
  // FS-1021: left over from the old unsubscribe link; see `REFUSAL_TEXT`.
  unsubscribed: 'Unsubscribed from HVAC system emails',
  test: 'Test address',
};

/**
 * R6 — "Not recorded" reads as a form field nobody filled in, and on the one
 * line above a list of reasons Send is off it looked like one of them. The
 * column is a record of what SES and the account have said about the
 * address, so an empty one says exactly that.
 */
export const addressStatusText = (status: string | null | undefined): string =>
  own(ADDRESS_STATUS_TEXT, status)
  || 'No record yet: this address has never been written to.';

/**
 * FS-646 — where confirming the homeowner's address has got to, as one line.
 *
 * "How well we know it" above is the send tier, which counts mail delivered
 * before as confirmation; this line says which kind of confirmation it is,
 * or how far the verification link and its two reminders have got. Dates are
 * Eastern like every other date on this tab.
 */
const verificationStateSentence = (v: RecipientVerification): string => {
  switch (v.state) {
    case 'owner_confirmed': {
      const seen = easternDate(v.confirmed_at, '');
      return seen
        ? `Confirmed by the owner (link clicked), first seen ${seen}`
        : 'Confirmed by the owner (link clicked)';
    }
    case 'provider_confirmed':
      return 'Confirmed by their Google or Apple sign-in';
    case 'confirmed_by_delivery':
      return 'Confirmed: we have delivered mail to this address before';
    case 'internal':
      return 'Internal FluxHive address';
    case 'link_sent': {
      const sent = easternDate(v.first_sent_at, 'on an unknown date');
      const due = easternDate(v.next_reminder_due_at, '');
      if (!due) return `Link sent ${sent}; waiting for the owner to click it`;
      return `Link sent ${sent}; reminder ${(v.server_reminders_sent ?? 0) + 1} of 2 due ${due}`;
    }
    case 'no_reminders_left':
      return 'No reminders left: the link and two reminders have gone out without a confirmation. '
        + 'The owner can still resend from the app.';
    case 'not_asked':
      return 'Not asked yet';
    default:
      return `Not known to this console (${String(v.state)})`;
  }
};

/**
 * FS-789 — the newest admin resend, when there is one, after the state. An
 * admin send is not a reminder, so it never changes the state's own words.
 * Most state sentences are phrases and take it after a semicolon; the one
 * that is already a full sentence takes it as another.
 */
export const verificationSentence = (v: RecipientVerification): string => {
  const base = verificationStateSentence(v);
  if (!v.last_admin_send_at) return base;
  const when = easternDate(v.last_admin_send_at, 'on an unknown date');
  const by = v.last_admin_send_by?.trim();
  const who = by ? ` by ${by}` : '';
  return base.endsWith('.')
    ? `${base} Admin resend ${when}${who}.`
    : `${base}; admin resend ${when}${who}`;
};

/** What each verification state means, for the line's mouseover. */
const VERIFICATION_TITLE: Record<string, string> = {
  owner_confirmed:
    'The owner clicked the verification link in the email from their account. '
    + 'The date is when FluxHive first saw it confirmed.',
  provider_confirmed:
    'The owner signs in with Google or Apple, and that provider vouches for the address. '
    + 'No link is needed.',
  confirmed_by_delivery:
    'Mail FluxHive sent to this address was delivered before, or the address was confirmed '
    + 'by hand. It counts as confirmed for sending.',
  internal: 'A FluxHive team address. It counts as confirmed for sending.',
  link_sent:
    'A verification link went out and has not been clicked yet. FluxHive sends up to two '
    + 'reminders: the first 4 days after the link, the second 10 days after the first.',
  no_reminders_left:
    'No reminders left: the link and two reminders have gone out without a confirmation. '
    + 'The owner can still resend from the app.',
  not_asked:
    'No verification link has gone to this address yet. The app asks after the owner sets up '
    + 'a working Clip, once verification is turned on.',
};

export const verificationTitle = (state: string): string =>
  own(VERIFICATION_TITLE, state)
  ?? 'A verification state this version of the console does not know. Reload for a newer one.';

/** FS-789 — what "admin resend" on the line means, for its mouseover. */
const ADMIN_RESEND_TITLE =
  'An admin resend is a verification email someone sent with the Send verification email button; '
  + 'the line shows the newest one and who sent it. It does not count against the two automatic '
  + 'reminders.';

/** The line's mouseover: the state, and the admin resend when there is one. */
export const verificationLineTitle = (v: RecipientVerification): string =>
  (v.last_admin_send_at
    ? `${verificationTitle(v.state)} ${ADMIN_RESEND_TITLE}`
    : verificationTitle(v.state));

/**
 * FS-789 — the two states where the button would do nothing: the address is
 * confirmed already, so the Lambda would refuse without sending. Every other
 * state, an unknown one included, leaves the button on.
 */
const VERIFICATION_NOT_NEEDED: Record<string, string> = {
  owner_confirmed: 'the owner already confirmed this address by clicking the verification link.',
  provider_confirmed:
    'the owner signs in with Google or Apple, which confirms the address without a link.',
};

const verificationNotNeeded = (v: RecipientVerification | null | undefined): string | null =>
  (v ? own(VERIFICATION_NOT_NEEDED, v.state) ?? null : null);

/** The button's mouseover: busy, why it is off, or what it does and the address status. */
const sendVerificationTitle = (recipient: NeedsRecipient, busy: boolean): string => {
  if (busy) return TOOLTIPS.sendVerificationBusy;
  const notNeeded = verificationNotNeeded(recipient.verification);
  if (notNeeded) return TOOLTIPS.sendVerificationNotNeeded(notNeeded);
  const status = addressStatusText(recipient.address_status).replace(/\.$/, '');
  return TOOLTIPS.sendVerification(`${status}.`);
};

/**
 * How well we know the address, which is what the tier actually means.
 *
 * R3 F27: `verified` is not "we have had mail from this address" — nobody
 * wrote to us, and that describes an inbox nothing here reads.
 *
 * R4 F32: it is not one thing either. `tier_for` takes a recorded
 * `verified`/`internal` status on the account OR an SES Delivery event on a
 * message we sent, so naming only the delivery called an account confirmed by
 * the first route unconfirmed.
 *
 * R6 carried the consequence on each line as well: R7 removed it. Operator
 * sends go to unconfirmed and relay addresses too, so a tier no longer says
 * whether FluxHive will write to the address — the only tier rule left is
 * the reputation gate's allowance, and that is the gate's own line, which
 * reads it from the gate rather than from the tier. Each line here says how
 * well the address is known and stops there.
 */
const CONFIDENCE_TEXT: Record<string, string> = {
  verified: 'Confirmed: by a recorded confirmation, or by mail we have delivered to this address before',
  plausible: 'Unconfirmed: the address looks real, but FluxHive has never delivered mail to it.',
  risky: 'Risky: a private relay address, or a domain FluxHive has never delivered to.',
};

const CHECKED_AT_DRAFT = 'Checked when a message is drafted';

export const confidenceText = (tier: string | null | undefined): string =>
  own(CONFIDENCE_TEXT, tier) || CHECKED_AT_DRAFT;

/** The same three words, for naming which addresses sending is open to. */
const CONFIDENCE_WORD: Record<string, string> = {
  verified: 'confirmed',
  plausible: 'unconfirmed',
  risky: 'risky',
};

/**
 * Every tier the gate can be open to.
 *
 * R6: listing all three ran out as "confirmed and unconfirmed and risky
 * addresses", which is the gate being wide open said in the most alarming
 * way available. Open to everything is one fact, so it gets one phrase.
 */
const ALL_TIERS = ['verified', 'plausible', 'risky'];

/**
 * Plan 123 R3 §6 — a reputation rate, as a percentage.
 *
 * The Lambda reports FRACTIONS: 0.04 is four per cent. Appending `%` to the
 * number displayed a 4% bounce rate as `0.04%`, which reads as a healthy
 * mailbox rather than a shut one. Null is not zero either — it is the gate
 * saying it could not read the number at all.
 */
export const gateRate = (rate: number | null | undefined): string =>
  (typeof rate === 'number' && Number.isFinite(rate)
    ? `${(rate * 100).toFixed(2)}%`
    : 'not available');

/** The gate could not read CloudWatch at all, which is not a rate problem. */
export const METRICS_UNAVAILABLE_SENTENCE =
  'Sending is paused: FluxHive could not read its mail-reputation numbers.';

/**
 * Plan 123 §6, R3 F26 — what the REPUTATION GATE says, and nothing else.
 *
 * This line is about the gate: which address tiers it is open to and the
 * rates behind that. It is deliberately independent of this recipient's tier
 * and of the deployment's send flag, both of which can hold a message back
 * while the gate is wide open — reading one off the other is how an
 * unreadable metric came to be reported as rates being too high.
 *
 * The two numbers are quoted in parentheses because they are what somebody
 * would be asked about, not the answer itself.
 */
export const sendingSentence = (
  gate: DraftGate | null | undefined,
): string => {
  if (!gate) return `${CHECKED_AT_DRAFT}.`;
  if ((gate.reason ?? '').startsWith('metrics_unavailable')) return METRICS_UNAVAILABLE_SENTENCE;
  const reading = `bounce ${gateRate(gate.bounce)}, complaint ${gateRate(gate.complaint)}`;
  const allowed = gate.allowed ?? [];
  if (allowed.length === 0) {
    return `Sending is paused for every address right now (${reading}).`;
  }
  if (ALL_TIERS.every((tier) => allowed.includes(tier))) {
    return `Sending is open to every address type right now (${reading}).`;
  }
  const open = allowed.map((t) => own(CONFIDENCE_WORD, t) ?? t).join(' and ');
  return `Sending is open to ${open} addresses right now (${reading}).`;
};

/**
 * Plan 123 R6 — one line saying what the reasons under it add up to.
 *
 * The block lists every condition separately, and an operator who has just
 * ticked two boxes reads the list as a report on something that already
 * happened. These say the state instead: Send is off, and whether clearing
 * anything here would turn it on.
 *
 * The deployment flag is the one reason that is not about this home, so it
 * gets its own line — "everything is in place" is true and worth saying, and
 * pointing the operator at reasons to clear would point at nothing.
 */
export const SEND_HELD_SUMMARY =
  'Send stays off for this home until the reasons below are cleared.';

export const SENDING_OFF_SUMMARY =
  'Everything is in place; sending is turned off on this deployment until FluxHive turns it on.';

/** Which of the two lines the live blockers call for, or neither. */
export const sendHoldSummary = (codes: string[]): string | null => {
  if (codes.length === 0) return null;
  return codes.every((code) => code === 'sending_disabled')
    ? SENDING_OFF_SUMMARY
    : SEND_HELD_SUMMARY;
};

const MissingTab: React.FC = () => {
  const { device, refetchDevice } = useWorkspaceDevice();
  // FS-838: whether there is a coil record for a coil row's tonnage to go on.
  const coilCount = device.app_context?.coil_count ?? null;
  const { hasPermission } = useAuth();
  const canContact = hasPermission(CONTACT_USERS_PERMISSION);
  const canEdit = hasPermission(EDIT_EQUIPMENT_PERMISSION);

  const [needs, setNeeds] = useState<EquipmentNeedsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [draft, setDraft] = useState<DraftResponse | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  /**
   * R5 — what "Try again" turns. The draft effect keys off the ticked rows,
   * so asking for the same rows again is not a change it would notice; this
   * counter is the change. Nothing else reads it.
   */
  const [draftAttempt, setDraftAttempt] = useState(0);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [edited, setEdited] = useState(false);
  // The draft effect must not re-run because the operator typed, so what it
  // needs to know about the typing is read off a ref.
  const editedRef = useRef(false);
  editedRef.current = edited;

  const [requestId, setRequestId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [refusal, setRefusal] = useState<RequestRefusal | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  /**
   * R3 F25 — a send that answered nothing and left nothing settled.
   *
   * Polling ran out, and whatever it found is not proof of anything: a row
   * may land a second later, one that is still sending may finish, a read
   * that failed may succeed. So the id is held, the operator is told which of
   * those it was (R4 F30), and the only thing on offer is another READ.
   * Sending is off until this is settled one way or the other.
   */
  const [unresolved, setUnresolved] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<RecoveryState>('absent');
  /** The last row any read of this request returned, or null if none has. */
  const [lastRow, setLastRow] = useState<RequestResultResponse | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkNote, setCheckNote] = useState<string | null>(null);

  const [drawer, setDrawer] = useState<DrawerTarget | null>(null);
  /** Why the drawer did not open: the record belongs to a Clip off this page. */
  const [drawerNote, setDrawerNote] = useState<string | null>(null);
  /** Targets whose `not_applicable` reply has been recorded, awaiting the edit. */
  const [absenceOffers, setAbsenceOffers] = useState<string[]>([]);
  const [replyBusy, setReplyBusy] = useState<string | null>(null);
  /** R5 — the failed reply and which target it was, so it is said next to it. */
  const [replyError, setReplyError] = useState<{ key: string; message: string } | null>(null);
  /**
   * FS-1021 — the Allow call in flight, and what it answered when it did not
   * simply turn emails on. Both name the device they belong to, the same way
   * the verification button's state below does (FS-789).
   */
  const [allowBusyFor, setAllowBusyFor] = useState<number | null>(null);
  const [allowResult, setAllowResult] = useState<
    { deviceId: number; code: string; text: string } | null
  >(null);
  /**
   * FS-789 — the Send verification email call in flight, and what it
   * answered. The answer outlives the reload that follows it, so the operator
   * still reads it once the Verification line has changed. Both name the
   * device they belong to: the route reuses this component when the operator
   * moves to another Clip, and one Clip's answer must never show on another.
   */
  const [verifyBusyFor, setVerifyBusyFor] = useState<number | null>(null);
  const [verifyResult, setVerifyResult] = useState<
    { deviceId: number; sent: boolean; text: string } | null
  >(null);
  const verifyBusy = verifyBusyFor === device.id;
  const allowBusy = allowBusyFor === device.id;
  const shownDevice = useRef(device.id);
  shownDevice.current = device.id;

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * FS-1021 — which read of the needs is the one the screen is waiting for.
   *
   * The route reuses this component when the operator moves to another Clip,
   * and a read started for one Clip can answer after the next Clip's has: an
   * Allow that finishes on Clip A starts A's reload, the operator opens B, B's
   * read lands first, and A's late answer would then put A's owner, setting
   * and parts on B's page. So every read carries the Clip it was for and its
   * place in line, and only the newest read for the Clip on screen may touch
   * the state: its answer, its failure and its end alike.
   */
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const forDevice = device.id;
    loadSeq.current += 1;
    const seq = loadSeq.current;
    const current = () =>
      alive.current && loadSeq.current === seq && shownDevice.current === forDevice;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchEquipmentNeeds(forDevice);
      if (!current()) return;
      setNeeds(data);
    } catch (err) {
      if (!current()) return;
      setNeeds(null);
      setLoadError(requestRefusal(err).message);
    } finally {
      if (current()) setLoading(false);
    }
  }, [device.id]);

  useEffect(() => {
    load();
  }, [load]);

  // FS-952 — a write from the equipment drawer is followed by the device's
  // projection stamp: the rows are read again once the projection has the
  // change, instead of three times on a timer.
  const { projectionStamp, followWrite } = useDeviceProjectionFollow(
    device.app_context?.projection_updated_at,
    refetchDevice,
    load,
  );

  // -------------------------------------------------------------------------
  // The rows
  // -------------------------------------------------------------------------

  const subjectClip = useMemo(
    () => needs?.clips.find((clip) => clip.is_subject) ?? needs?.clips[0] ?? null,
    [needs],
  );

  /**
   * The shared machines once, then a filter per Clip.
   *
   * A furnace belongs to the record, so two Clips on one home are asking
   * about the same machine and it is listed once. A filter is per Clip, and
   * the only way to tell two of them apart is the device id.
   */
  const rows: TargetRow[] = useMemo(() => {
    if (!needs || !subjectClip) return [];
    const shared = subjectClip.components
      .filter((component) => component.component !== 'filter')
      .map((component) => ({
        key: rowKey(component.component, null),
        component: component.component,
        deviceId: subjectClip.device_id,
        deviceName: subjectClip.device_name,
        filterDeviceId: null,
        label: rowLabel(component.component, null),
        data: component,
      }));
    const filters = needs.clips.flatMap((clip) =>
      clip.components
        .filter((component) => component.component === 'filter')
        .map((component) => ({
          key: rowKey('filter', clip.device_id),
          component: 'filter' as ComponentName,
          deviceId: clip.device_id,
          deviceName: clip.device_name,
          filterDeviceId: clip.device_id,
          label: rowLabel('filter', clip.device_id),
          data: component,
        })),
    );
    return [...shared, ...filters];
  }, [needs, subjectClip]);

  const selectedRows = useMemo(
    () => selected.map((key) => rows.find((row) => row.key === key)).filter((row): row is TargetRow => !!row),
    [selected, rows],
  );

  /**
   * The Clip a record belongs to, as the drawer reads a device: by id and
   * serial. `null` means the subject, and an id this account cannot see
   * resolves to nothing rather than to the subject — editing the wrong
   * household's record is worse than not opening the drawer.
   */
  const clipDevice = useCallback(
    (deviceId: number | null | undefined): { id: number; name: string } | null => {
      if (deviceId === null || deviceId === undefined || deviceId === device.id) {
        return { id: device.id, name: device.name };
      }
      const clip = needs?.clips.find((c) => c.device_id === deviceId);
      return clip ? { id: clip.device_id, name: clip.device_name } : null;
    },
    [device, needs],
  );

  const openDrawer = (target: DrawerTarget) => {
    setDrawerNote(null);
    setDrawer(target);
  };

  /**
   * R11 — there is no askable test any more.
   *
   * R7 read the server's `askable` and shut the box when it was false; R2
   * before it worked the answer out from the state. Both were the console
   * deciding what an operator is allowed to want. The flag is still on the
   * wire for one release and nothing here reads it: every part of the system
   * is a valid target, and what the screen knows about it is a note.
   */
  const toggle = (row: TargetRow) =>
    setSelected((prev) =>
      prev.includes(row.key) ? prev.filter((key) => key !== row.key) : [...prev, row.key],
    );

  // -------------------------------------------------------------------------
  // The draft
  // -------------------------------------------------------------------------

  const askedFor = useMemo(() => targetsFromKeys(selected), [selected]);

  useEffect(() => {
    if (!canContact) return undefined;
    if (selected.length === 0) {
      setDraft(null);
      setDraftError(null);
      if (!editedRef.current) {
        setSubject('');
        setBody('');
      }
      return undefined;
    }
    let cancelled = false;
    // Read off the keys, not off the memo: a memo React chose to drop would
    // re-run this effect and render a second draft for the same tick.
    const { components, filterDeviceIds } = targetsFromKeys(selected);
    setDraftBusy(true);
    setDraftError(null);
    renderEquipmentDraft(device.id, {
      components,
      filter_device_ids: filterDeviceIds,
    })
      .then((rendered) => {
        if (cancelled || !alive.current) return;
        setDraft(rendered);
        // Re-ticking re-renders the draft only while it is still ours to
        // replace. Once an operator has typed, their words stay and "Reset to
        // draft" is offered instead.
        if (!editedRef.current) {
          setSubject(rendered.subject);
          setBody(rendered.body_text);
        }
      })
      .catch((err) => {
        if (cancelled || !alive.current) return;
        setDraft(null);
        // R5 — a failed draft is not a failed send, and it is the draft
        // route's own sentence that says so.
        setDraftError(draftRefusal(err).message);
      })
      .finally(() => {
        if (!cancelled && alive.current) setDraftBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [device.id, canContact, selected, draftAttempt]);

  /**
   * Ask for the same draft again. It renders a message; it sends nothing.
   *
   * The failed sentence goes now rather than when the effect gets there: the
   * operator pressed the button, and a retry that lands in the same tick
   * would otherwise leave the old error standing under the new draft.
   */
  const retryDraft = () => {
    setDraftError(null);
    setDraftAttempt((attempt) => attempt + 1);
  };

  const resetDraft = () => {
    if (!draft) return;
    setSubject(draft.subject);
    setBody(draft.body_text);
    setEdited(false);
  };

  const subjectError = useMemo(() => {
    if (!draft) return null;
    if (/[\r\n]/.test(subject)) return 'Keep the subject on one line.';
    if (subject.length > SUBJECT_MAX) return `The subject is over ${SUBJECT_MAX} characters.`;
    if (subject.trim() === '') return 'The subject cannot be empty.';
    return null;
  }, [draft, subject]);

  const bodyError = useMemo(() => {
    if (!draft) return null;
    if (body.length > BODY_MAX) return `The message is over ${BODY_MAX.toLocaleString()} characters.`;
    if (body.trim() === '') return 'The message cannot be empty.';
    return null;
  }, [draft, body]);

  // -------------------------------------------------------------------------
  // What stops a send
  // -------------------------------------------------------------------------

  /**
   * Every reason the server would refuse this send, gathered from the three
   * places that know one: the record, the system, and the draft the renderer
   * just wrote. Each is shown as a sentence, and any one of them disables
   * Send. The server checks every one of them again.
   *
   * FS-1021: the owner's email setting, from the needs block and from the
   * draft, is one of them by its state as well as by its blocker.
   */
  const blockers = useMemo(() => {
    const all: Blocker[] = [
      ...(needs?.recipient?.blockers ?? []),
      ...(needs?.system_blockers ?? []),
      ...(draft?.recipient.blockers ?? []),
      ...selectedRows.flatMap((row) => row.data.send_blockers ?? []),
    ];
    return dedupeBlockers(withPreferenceBlockers(all, [
      needs?.recipient?.email_preference?.state,
      draft?.recipient.email_preference,
    ]));
  }, [needs, draft, selectedRows]);

  /**
   * R6 — the line under "Who this goes to", saying what the reasons listed
   * under it add up to.
   *
   * R8 — every blocker on screen is a blocker that holds. The internal-test
   * override that used to let one of them be listed without holding anything
   * is gone (Chris, 2026-09-21), so the line is read off the same list the
   * operator is looking at.
   */
  const holdSummary = useMemo(
    () => sendHoldSummary(blockers.map(blockerCode)),
    [blockers],
  );

  const canSend =
    canContact
    && !!draft
    && !draftBusy
    && selected.length > 0
    && !subjectError
    && !bodyError
    && blockers.length === 0
    && !sending;
  // R11 — `unresolved` is NOT in that list any more. An attempt nobody could
  // account for is a warning (the panel says a repeat may send twice), not a
  // lock: "Maybe they lost the first to a spam filter" (Chris, 2026-09-22).

  /**
   * R11 — what the Send button says on mouseover, enabled or not.
   *
   * A disabled control with no explanation is the thing Chris hit first, so
   * the reasons are ordered the way an operator would work through them: the
   * account, then anything the server would refuse, then the two ordinary
   * "not yet" states, then the fields.
   */
  const sendTitle = (): string => {
    if (canSend) {
      return TOOLTIPS.send(
        needs?.recipient?.address ?? draft?.recipient.address ?? 'the homeowner',
      );
    }
    if (!canContact) return TOOLTIPS.sendUnavailable('your account cannot write to homeowners.');
    if (blockers.length > 0) return blockerSentence(blockers[0]);
    if (selected.length === 0) return TOOLTIPS.sendNothingTicked;
    if (!draft || draftBusy) return TOOLTIPS.sendDraftPending;
    if (subjectError) return TOOLTIPS.sendUnavailable(lowerFirst(subjectError));
    if (bodyError) return TOOLTIPS.sendUnavailable(lowerFirst(bodyError));
    return TOOLTIPS.sendInFlight;
  };

  // -------------------------------------------------------------------------
  // Sending
  // -------------------------------------------------------------------------

  const finish = (state: string, replayed?: boolean) => {
    setOutcome(outcomeSentence(state, replayed));
    setRequestId(null);
    setUnresolved(null);
    setRecovery('absent');
    setLastRow(null);
    setCheckNote(null);
    setSelected([]);
    setDraft(null);
    setSubject('');
    setBody('');
    setEdited(false);
    // The ticked components are `requested` now, with today's date on them.
    load();
    refetchDevice?.();
  };

  /**
   * A send that never answered is READ BACK, not repeated. Every way out of
   * this loop leaves the request exactly as it was, because the alternative —
   * posting it again to find out — is how an unaccounted attempt becomes a
   * second message.
   *
   * R4 F30: the loop reports WHICH of the three it was, and keeps the last
   * row it saw. A non-terminal 200 is a row: the request was accounted for
   * and has not finished, which is a different fact from finding nothing.
   */
  const pollResult = async (id: string): Promise<PollOutcome> => {
    let row: RequestResultResponse | null = null;
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
      /* eslint-disable no-await-in-loop */
      if (attempt > 0) await wait(POLL_MS);
      if (!alive.current) break;
      try {
        const result = await fetchRequestResult(device.id, id);
        row = result;
        if (isTerminalResult(result)) return { state: 'terminal', row: result };
      } catch (err) {
        // A 404 means the request has not landed yet, or never will. Either
        // way the answer is to look again, never to send again. Anything
        // else — a 5xx, a lost connection — is a read that FAILED, and a
        // failed read establishes nothing at all.
        if ((err as any)?.response?.status !== 404) return { state: 'unreadable', row };
      }
      /* eslint-enable no-await-in-loop */
    }
    return { state: row ? 'recorded' : 'absent', row };
  };

  /**
   * Hand the attempt to the recovery panel: the id is kept, and the panel
   * says which of the three outcomes this was. No path out of a send that
   * did not settle skips this (F30).
   */
  const openRecovery = (id: string, state: RecoveryState, row: RequestResultResponse | null) => {
    setUnresolved(id);
    setRecovery(state);
    setLastRow(row);
    setCheckNote(null);
  };

  /**
   * Send request, in one click (R9, Chris 2026-09-22). The confirmation step
   * this used to go through was overkill for sending an email.
   *
   * R2 F10 survives as `attempt`: the payload is built from the state AT THE
   * CLICK and posted, read back and recovered as that one object. Nothing
   * downstream — a retry, a poll, a recovery — re-reads the live state, so a
   * draft render landing mid-send cannot change what went out.
   */
  const sendNow = async () => {
    // A draft still being written has nothing to send, and the one on screen
    // is about to be replaced.
    if (!draft || draftBusy || !canSend) return;
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setRefusal(null);
    // Minted here and kept: a double-click, a refusal and a retry, and a send
    // that never answered are all the same request. It is cleared again only
    // when the server has definitively refused this one (F12).
    const id = requestId ?? newRequestId();
    setRequestId(id);
    const attempt: { payload: SendRequestBody } = {
      payload: {
        request_id: id,
        components: askedFor.components,
        filter_device_ids: askedFor.filterDeviceIds,
        system_key: draft.system_key,
        claim_id: draft.claim_id,
        observed_at: draft.observed_at,
        subject,
        body_text: body,
      },
    };
    try {
      const result = await sendEquipmentRequest(device.id, attempt.payload);
      if (!alive.current) return;
      finish(result.state, result.replayed);
    } catch (err) {
      if (!alive.current) return;
      // A definitive refusal — a 4xx carrying the application's own code — is
      // the server's last word on THIS attempt: show it, and let go of the id
      // so a corrected send is a new request rather than a replay of the
      // refused row (F12). Nothing else says anything of the kind: a hop
      // failure, a 503, a 500 and a lost response all leave the send in
      // doubt, and the id is what recovers it (F11, R3 F25).
      if (!isAmbiguousSendOutcome(err)) {
        setRefusal(requestRefusal(err));
        setRequestId(null);
        return;
      }
      try {
        const poll = await pollResult(attempt.payload.request_id);
        if (!alive.current) return;
        if (poll.state === 'terminal' && poll.row) {
          if (poll.row.status === 'refused') {
            const code = poll.row.refusal ?? 'refused';
            setRefusal({
              code,
              message: refusalSentence(code, null, { withCode: false }),
            });
            setRequestId(null);
            return;
          }
          if (poll.row.send) {
            finish(poll.row.send.state);
            return;
          }
        }
        // Nothing settled it. Six 404s establish only that nothing has been
        // found yet; six 200s on a sending row establish that the attempt IS
        // recorded; a read that failed establishes neither (F25, R4 F30).
        // All three keep the id and leave one action on offer, which reads
        // the request again.
        openRecovery(
          attempt.payload.request_id,
          poll.state === 'unreadable' ? 'unreadable' : (poll.row ? 'recorded' : 'absent'),
          poll.row,
        );
      } catch {
        if (!alive.current) return;
        // The loop itself came apart. Still not a refusal, and still not a
        // reason to post the send again.
        openRecovery(attempt.payload.request_id, 'unreadable', null);
      }
    } finally {
      sendingRef.current = false;
      if (alive.current) setSending(false);
    }
  };

  /**
   * R3 F25 — the only recovery action, and it is a GET.
   *
   * One read of the result route. A row that reached a terminal state ends
   * the wait; anything else leaves the request exactly as it was, because
   * the alternative — posting the send again to "check" it — is how an
   * unrecorded attempt becomes a first send.
   */
  const checkStatus = async () => {
    if (!unresolved || checking) return;
    setChecking(true);
    setCheckNote(null);
    try {
      const result = await fetchRequestResult(device.id, unresolved);
      if (!alive.current) return;
      if (result.status === 'refused') {
        const code = result.refusal ?? 'refused';
        setRefusal({ code, message: refusalSentence(code, null, { withCode: false }) });
        setRequestId(null);
        setUnresolved(null);
        return;
      }
      if (result.send && isTerminalResult(result)) {
        finish(result.send.state);
        return;
      }
      // The row is there — a 200 means the request was accounted for — but it
      // has not finished. That is a different fact from finding nothing.
      setLastRow(result);
      setRecovery('recorded');
      setCheckNote(RECOVERY_TEXT.recorded);
    } catch (err) {
      if (!alive.current) return;
      if ((err as any)?.response?.status === 404) {
        // R4 F30 — a 404 now does not unsay a row an earlier read returned.
        setCheckNote(lastRow ? RECORDED_BUT_NOT_FOUND : NOTHING_RECORDED);
        return;
      }
      // A read that failed is not a refusal, and it says nothing about the
      // attempt either way. `requestRefusal` would have called this GET's own
      // 500 a refused send.
      setRecovery('unreadable');
      setCheckNote(RECOVERY_TEXT.unreadable);
    } finally {
      if (alive.current) setChecking(false);
    }
  };

  /** Give up on the unresolved attempt: the id is released, nothing is sent. */
  const cancelUnresolved = () => {
    setUnresolved(null);
    setRecovery('absent');
    setLastRow(null);
    setCheckNote(null);
    setRequestId(null);
  };

  // -------------------------------------------------------------------------
  // Replies
  // -------------------------------------------------------------------------

  const targetKey = (row: RequestHistoryRow, target: RequestTarget) =>
    `${row.request_id}:${target.component}:${target.device_id ?? ''}`;

  const reply = async (
    row: RequestHistoryRow,
    target: RequestTarget,
    chosen: ReplyOutcome,
  ) => {
    const key = targetKey(row, target);
    setReplyBusy(key);
    setReplyError(null);
    setDrawerNote(null);
    try {
      await recordRequestReply(device.id, row.request_id, {
        component: target.component,
        ...(target.component === 'filter' && target.device_id !== null
          ? { device_id: target.device_id }
          : {}),
        outcome: chosen,
      });
      if (!alive.current) return;
      if (chosen === 'photo_received' && canEdit) {
        // The photo is filed against the Clip the target names, which from a
        // subject's workspace can be a sibling.
        const on = clipDevice(target.device_id);
        if (on) {
          openDrawer({ device: on, tab: own(PHOTO_RECORD, target.component) ?? 'hvac' });
        } else {
          setDrawerNote(
            `The photo goes on device ${target.device_id}, which is not on this page. `
            + 'Open that device to upload it.',
          );
        }
      }
      // §8.6 — the absence is a SECOND, deliberate click, so a misread reply
      // cannot quietly retire a component.
      if (chosen === 'not_applicable' && ABSENCE_COMPONENTS.includes(target.component) && canEdit) {
        setAbsenceOffers((prev) => (prev.includes(key) ? prev : [...prev, key]));
      }
      await load();
    } catch (err) {
      // R5 — a hop failure here recorded no reply and sent nothing, and the
      // sentence is shown on the target whose button was pressed.
      if (alive.current) setReplyError({ key, message: replyRefusal(err).message });
    } finally {
      if (alive.current) setReplyBusy(null);
    }
  };

  // -------------------------------------------------------------------------
  // FS-1021 — allowing HVAC system emails again
  // -------------------------------------------------------------------------

  /**
   * One click, no confirmation (R11: the operator drives). The call names the
   * address and the app account on screen, and the server turns the setting
   * on only if it is still off for that same owner. Nothing about the address
   * changes, so the address status and the Verification line stay as they are.
   *
   * A change, `already_on` and `refresh_required` all leave the tab out of
   * date, so each reloads the needs AND asks for the draft again (r2 F39): a
   * draft rendered while emails were off carries an `email_off` blocker that
   * reloading the needs alone would leave holding Send. The new draft keeps
   * whatever the operator typed, because the draft effect replaces the subject
   * and message only while they are untouched.
   *
   * `preference_unavailable`, or no answer at all, is a sentence and nothing
   * more. Whether the change was made is not known, and pressing Allow again
   * is safe: a second call that finds emails on answers `already_on`.
   *
   * An answer that arrives after the operator has moved to another Clip is
   * dropped, as the verification button's is (FS-789): `load` here is the
   * clicked Clip's load, and running it would put that Clip's needs on the
   * page of the one now shown.
   */
  const allowEmails = async (address: string, uid: string | null | undefined) => {
    const forDevice = device.id;
    const stillShown = () => alive.current && shownDevice.current === forDevice;
    const refresh = async () => {
      setDraftAttempt((attempt) => attempt + 1);
      await load();
    };
    setAllowBusyFor(forDevice);
    setAllowResult(null);
    try {
      await resubscribeAddress(forDevice, address, uid);
      if (!stillShown()) return;
      await refresh();
    } catch (err) {
      if (!stillShown()) return;
      const refusal = resubscribeRefusal(err);
      setAllowResult({ deviceId: forDevice, code: refusal.code, text: refusal.message });
      if (ALLOW_ANSWERS_THAT_RELOAD.has(refusal.code)) await refresh();
    } finally {
      if (alive.current) setAllowBusyFor((cur) => (cur === forDevice ? null : cur));
    }
  };

  // -------------------------------------------------------------------------
  // FS-789 — sending the verification link
  // -------------------------------------------------------------------------

  /**
   * One click, no confirmation (R11: the operator drives). A send reloads
   * the tab so the Verification line shows it; so does a refusal that
   * recorded the owner as confirmed. Any other refusal changed nothing, so it
   * is only a sentence beside the button.
   */
  const sendVerification = async (address: string) => {
    const forDevice = device.id;
    // Still on the Clip that was clicked? `load` here is that Clip's load.
    const stillShown = () => alive.current && shownDevice.current === forDevice;
    setVerifyBusyFor(forDevice);
    setVerifyResult(null);
    try {
      const sent = await sendVerificationEmail(forDevice, address);
      if (!stillShown()) return;
      setVerifyResult({
        deviceId: forDevice,
        sent: true,
        text: verificationEmailSentSentence(sent.address || address, sent.sent_at),
      });
      await load();
    } catch (err) {
      if (!stillShown()) return;
      const refusal = verificationEmailRefusal(err);
      setVerifyResult({ deviceId: forDevice, sent: false, text: refusal.message });
      if (refusal.verified_recorded) await load();
    } finally {
      if (alive.current) setVerifyBusyFor((cur) => (cur === forDevice ? null : cur));
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading && !needs) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-md p-6">
        <p className="text-sm text-muted-foreground">Loading what is missing…</p>
      </div>
    );
  }

  if (!loadError && needs && !subjectClip) {
    return (
      <div data-testid="missing-tab-empty" className="bg-card border border-border rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold">Missing information</h3>
        <p className="mt-1 text-sm text-gray-600">
          No Clips are on file for this system, so there is no missing information to show yet.
        </p>
      </div>
    );
  }

  if (loadError || !needs) {
    return (
      <div className="bg-card border border-border rounded-lg shadow-md p-6">
        <p className="text-sm text-red-700" role="alert">
          {loadError ?? 'What is missing about this system could not be loaded.'}
        </p>
        <Button
          variant="secondary"
          title={TOOLTIPS.retryLoad}
          onClick={load}
          className="mt-3"
        >
          Try again
        </Button>
      </div>
    );
  }

  const recipient = needs.recipient ?? null;
  /** FS-1021 — the owner's setting as the needs block read it; absent on an older API. */
  const preferenceState = recipient?.email_preference?.state;
  /**
   * Unreadable by either read. A draft that could not read it says so as a
   * blocker, and this line is where "try again" is said (no blocker says it).
   */
  const preferenceUnreadable =
    preferenceState === 'unavailable' || draft?.recipient.email_preference === 'unavailable';
  const allowShown = !!allowResult && allowResult.deviceId === device.id;
  const history = needs.history ?? [];
  /** R11 — what to know about this system before writing to it. Never a gate. */
  const systemNotes = notesOf(needs.system_notes);
  /** R11 — and about the address, from the record and from the draft alike. */
  const recipientNotes = notesOf([
    ...(recipient?.notes ?? []),
    ...(draft?.recipient.notes ?? []),
  ]);
  /** The one thing that shuts every box on the tab (R11). */
  const accountHelp = photoRequestHelp(canContact);
  /**
   * R3 F27 — the headline counts the rows ON SCREEN.
   *
   * `needs.score` is the SUBJECT Clip's score, and the list below also holds
   * every sibling's filter. A subject with nothing missing and a sibling
   * filter that needs a photo headlined "everything is on file" over a row
   * saying it is not. Numerator and denominator now come from the same list.
   */
  const stillNeeded = rows.filter((row) => row.data.counts_toward_score).length;
  /** §3 — blockers the API dropped because they belong to a Clip off this page. */
  const hiddenBlockers = Math.max(
    needs.hidden_blocker_count ?? 0,
    draft?.hidden_blocker_count ?? 0,
  );

  return (
    <div data-testid="missing-tab" className="bg-card border border-border rounded-lg shadow-md p-6 space-y-6">
      <header>
        <h3 className="text-lg font-semibold">Missing information</h3>
        <p data-testid="missing-headline" className="mt-1 text-sm text-muted-foreground">
          {stillNeeded === 0
            ? 'Everything the analysis needs is on file for this system.'
            : `${stillNeeded} of ${rows.length} parts of this system still need information.`}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Every time on this tab is {EASTERN_ZONE_LABEL}.
        </p>
        {/* R11 — what the operator should know about this system, at the top
            of the tab and in front of every decision below it. None of it
            stops anything. */}
        {systemNotes.length > 0 && (
          <div data-testid="system-notes" className="mt-2">
            <NoteLines notes={systemNotes} />
          </div>
        )}
      </header>

      {/* 1 — Components --------------------------------------------------- */}
      <section data-testid="missing-components">
        <h4 className="font-medium">Parts of this system</h4>
        {needs.siblings_hidden > 0 && (
          <p data-testid="siblings-hidden-note" className="mt-1 text-xs text-muted-foreground">
            {needs.siblings_hidden} Clip
            {needs.siblings_hidden === 1 ? ' in this home is' : 's in this home are'} outside what
            you can see. {needs.siblings_hidden === 1 ? 'Its parts are' : 'Their parts are'} neither
            listed here nor counted above.
          </p>
        )}
        <ul className="mt-2 divide-y divide-gray-100">
          {rows.map((row) => {
            const { data } = row;
            const request = data.request ?? null;
            const requestedOn = request
              ? easternDate(request.sent_at ?? request.requested_at, '')
              : null;
            const sendBlockers = data.send_blockers ?? [];
            const rowNotes = notesOf(data.notes);
            const part = rowPart(row.component, row.filterDeviceId);
            const isBlocked = data.state === 'blocked';
            const chip = isBlocked
              ? blockedChip(data.reason)
              : own(STATE_LABEL, data.state) ?? data.state;
            const chipClass = chip === NEEDS_A_LOOK
              ? LOOK_CLASS
              : own(STATE_CLASS, data.state) ?? 'border border-status-neutral-border bg-status-neutral text-status-neutral-foreground';
            /**
             * R7 — why a needed part is waiting, read off the fact that is
             * actually unknown. A component can carry a settled fact beside
             * an unsettled one, and the settled one's `on_file` route says
             * nothing about what is still wanted.
             */
            const neededReason = data.state === 'needed'
              ? data.facts
                .filter((fact) => !fact.known)
                .map((fact) => neededSentence(
                  fact.route_reason,
                  modelNumberFromEvidence([fact.evidence]),
                ))
                .find((sentence): sentence is string => !!sentence) ?? null
              : null;
            return (
              <li
                key={row.key}
                data-testid="component-row"
                data-component={row.key}
                className="py-3"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{row.label}</span>
                  <span
                    data-testid="state-chip"
                    className={`rounded-sm px-1.5 py-0.5 text-xs font-medium ${chipClass}`}
                  >
                    {chip}
                    {data.state === 'requested' && requestedOn ? ` on ${requestedOn}` : ''}
                  </span>
                </div>

                {data.facts.map((fact) => (
                  <div key={fact.field} className="mt-1 text-sm">
                    {/* Value, state and route are one sentence of their own,
                        separated by middle dots. Two adjacent spans used to
                        run "unknown" into "Missing" and read as one word. The
                        route reason is left off when the sentence below the
                        row already says it. */}
                    <span className="text-gray-700">
                      {missingFieldLabel(fact.field)}:{' '}
                      {fact.value
                        ? `${fact.value}${fact.unit ? ` ${fact.unit}` : ''}`
                        : 'unknown'}
                      {' · '}
                      <span className="text-xs text-muted-foreground">
                        {lowerFirst(stateLabel(fact.fact_state))}
                        {' · '}
                        {routeLabel(fact.route)}
                        {isBlocked && fact.route_reason === data.reason
                          ? ''
                          : ` · ${routeReasonLabel(fact.route_reason)}`}
                      </span>
                    </span>
                    {fact.evidence && (
                      <span className="mt-0.5 block text-xs text-muted-foreground whitespace-pre-wrap wrap-break-word">
                        {fact.evidence}
                      </span>
                    )}
                  </div>
                ))}

                {isBlocked && (
                  <p data-testid="blocked-reason" className="mt-1 text-sm text-gray-800">
                    {blockedSentence(
                      data.reason,
                      modelFromEvidence(data.facts.map((fact) => fact.evidence)),
                    )}{' '}
                    <Link
                      to={`/equipment-inventory?device_id=${row.deviceId}`}
                      title={TOOLTIPS.openLink(EQUIPMENT_INVENTORY)}
                      className="text-link hover:underline"
                    >
                      Open the Equipment Inventory
                    </Link>
                  </p>
                )}

                {/* R7 — a needed row says which homeowner answer would
                    settle it, in the same place and the same voice a blocked
                    row says whose turn it is. */}
                {neededReason && (
                  <p data-testid="needed-reason" className="mt-1 text-sm text-gray-800">
                    {neededReason}
                  </p>
                )}

                {sendBlockers.map((blocker) => (
                  <p
                    key={blockerKey(blocker)}
                    data-testid="target-blocker"
                    className="mt-1 text-sm text-gray-700"
                  >
                    {blockerSentence(blocker)}
                  </p>
                ))}

                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      aria-label={`Request a photo of ${rowPhrase(row.component, row.filterDeviceId)}`}
                      aria-describedby={`request-photo-help-${row.key}`}
                      title={TOOLTIPS.requestPhoto(part)}
                      checked={selected.includes(row.key)}
                      // R11 — the ONLY thing that shuts this box. Every part
                      // of the system, in every state, is the operator's to
                      // ask for (Chris, 2026-09-22).
                      disabled={!canContact || sending}
                      onCheckedChange={() => toggle(row)}
                    />
                    Request a photo
                  </label>
                  <span id={`request-photo-help-${row.key}`} className="text-xs text-muted-foreground">
                    {TOOLTIPS.requestPhoto(part)}
                  </span>

                  {canEdit && ABSENCE_COMPONENTS.includes(row.component) && (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={`Declare that this system has no ${componentPhrase(row.component)}`}
                      title={TOOLTIPS.notOnSystem(componentPhrase(row.component))}
                      onClick={() => openDrawer({
                        device: { id: row.deviceId, name: row.deviceName },
                        ...ABSENCE_EDIT,
                      })}
                    >
                      Not on this system
                    </Button>
                  )}

                  {canEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={`Enter ${rowPhrase(row.component, row.filterDeviceId)} by hand`}
                      title={TOOLTIPS.enterByHand(handEntryFor(data, coilCount).fact)}
                      onClick={() => openDrawer({
                        device: { id: row.deviceId, name: row.deviceName },
                        tab: handEntryFor(data, coilCount).tab,
                        field: handEntryFor(data, coilCount).field,
                      })}
                    >
                      Enter by hand
                    </Button>
                  )}
                </div>

                {/* R11 — the line under the box was the one that explained
                    why the box was off. Nothing about the part turns it off
                    now, so what goes here instead is what an operator would
                    want to know before ticking it: already on file, asked
                    for last week, the homeowner said no. Each is a sentence,
                    and none of them disables anything. */}
                <NoteLines notes={rowNotes} />

                {/* The one thing that does shut every box: the account. */}
                {accountHelp && (
                  <p data-testid="photo-request-help" className="mt-1 text-xs text-muted-foreground">
                    {accountHelp}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* 2 — Recipient ---------------------------------------------------- */}
      {canContact && (
        <section data-testid="missing-recipient" className="border-t pt-4">
          <h4 className="font-medium">Who this goes to</h4>
          {/* R6: what the block adds up to, before the details that make it
              up. Nothing has been sent at this point and nothing here says
              otherwise. */}
          {holdSummary && (
            <p data-testid="recipient-summary" className="mt-1 text-sm text-gray-700">
              {holdSummary}
            </p>
          )}
          <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div data-testid="recipient-address">
              <dt className="inline text-muted-foreground">Address: </dt>
              <dd className="inline font-mono">{recipient?.address ?? 'none on file'}</dd>
            </div>
            <div data-testid="recipient-status">
              <dt className="inline text-muted-foreground">Address status: </dt>
              <dd className="inline">{addressStatusText(recipient?.address_status)}</dd>
            </div>
            <div data-testid="recipient-tier">
              <dt className="inline text-muted-foreground">How well we know it: </dt>
              <dd className="inline">{confidenceText(draft?.recipient.tier)}</dd>
            </div>
            {recipient?.verification && (
              <div
                data-testid="recipient-verification"
                title={verificationLineTitle(recipient.verification)}
              >
                <dt className="inline text-muted-foreground">Verification: </dt>
                <dd className="inline">{verificationSentence(recipient.verification)}</dd>
              </div>
            )}
            <div data-testid="recipient-gate">
              <dt className="inline text-muted-foreground">Sending: </dt>
              <dd className="inline">
                {sendingSentence(draft?.recipient.gate)}
              </dd>
            </div>
            <div data-testid="recipient-last-request">
              {/* F27: the column records every ATTEMPT, sent or not. */}
              <dt className="inline text-muted-foreground">Last request to this home: </dt>
              <dd className="inline">
                {easternDate(
                  draft?.recipient.last_request_at ?? recipient?.last_request_at,
                  'never asked',
                )}
              </dd>
            </div>
          </dl>

          {/* FS-789 — the standard verification link, sent on one click. Off
              only when the address is confirmed already; the answer stays
              beside the button through the reload that follows it. */}
          {recipient?.address && (
            <div className="mt-2 flex flex-wrap items-start gap-x-3 gap-y-1">
              <Button
                variant="secondary"
                size="sm"
                data-testid="send-verification"
                title={sendVerificationTitle(recipient, verifyBusy)}
                disabled={verifyBusy || !!verificationNotNeeded(recipient.verification)}
                onClick={() => sendVerification(recipient.address as string)}
              >
                {verifyBusy ? 'Sending…' : 'Send verification email'}
              </Button>
              {verifyResult && verifyResult.deviceId === device.id && (
                <p
                  data-testid="send-verification-result"
                  className={`w-full text-sm ${verifyResult.sent ? 'text-green-800' : 'text-red-700'}`}
                  role={verifyResult.sent ? 'status' : 'alert'}
                >
                  {verifyResult.text}
                </p>
              )}
            </div>
          )}

          {/* FS-1021 — the owner's HVAC system emails setting, and the way
              back when they ask for it. Off and unreadable each get a line;
              on says nothing, like an address with nothing against it. The
              Allow answer stays beside the button through the reload that
              follows it, and only on the Clip it was for. */}
          {(preferenceState === 'off' || preferenceUnreadable || allowShown) && (
            <div
              data-testid="recipient-email-preference"
              className="mt-2 flex flex-wrap items-start gap-x-3 gap-y-1"
            >
              {preferenceState === 'off' && (
                <p data-testid="recipient-email-off" className="text-sm text-gray-700">
                  {emailOffLine(recipient?.link_unsubscribed_at)}
                </p>
              )}
              {preferenceUnreadable && (
                <p data-testid="recipient-email-unavailable" className="text-sm text-gray-700">
                  {EMAIL_PREFERENCE_UNAVAILABLE_TEXT}
                </p>
              )}
              {preferenceState === 'off' && recipient?.address && (
                <Button
                  variant="secondary"
                  size="sm"
                  data-testid="resubscribe"
                  title={allowBusy ? TOOLTIPS.resubscribeBusy : TOOLTIPS.resubscribe}
                  disabled={allowBusy}
                  onClick={() => allowEmails(recipient.address as string, recipient.owner_uid)}
                >
                  {ALLOW_EMAILS_LABEL}
                </Button>
              )}
              {allowShown && allowResult && (
                <p
                  data-testid="resubscribe-result"
                  className={`w-full text-sm ${
                    ALLOW_ANSWERS_THAT_RELOAD.has(allowResult.code) ? 'text-gray-700' : 'text-red-700'
                  }`}
                  role={ALLOW_ANSWERS_THAT_RELOAD.has(allowResult.code) ? 'status' : 'alert'}
                >
                  {allowResult.text}
                </p>
              )}
            </div>
          )}

          {/* R13 — an address status of `unsubscribed` left from the old
              unsubscribe link, and why. Nothing writes one now; one that is
              left still holds Send, and the reason stays on the record. */}
          {recipient?.address_status === 'unsubscribed' && (
            <p data-testid="recipient-unsubscribe-why" className="mt-2 text-sm text-gray-700">
              {`Stopped ${easternDate(recipient.address_judged_at, 'on an unknown date')} `
                + `via ${unsubscribedVia(recipient.address_judged_by)}`}
              {unsubscribeWhy(recipient.address_note ?? null)
                ? `: ${unsubscribeWhy(recipient.address_note ?? null)}`
                : ''}
            </p>
          )}

          {/* R11 — what to know about this address. A test account, an ask
              that went out last week: worth saying, never a refusal. */}
          <NoteLines notes={recipientNotes} />

          {blockers.length > 0 && (
            <ul className="mt-3 space-y-1">
              {blockers.map((blocker) => (
                <li
                  key={blockerKey(blocker)}
                  id={blocker === blockers[0] ? 'missing-send-blocker' : undefined}
                  data-testid="send-blocker"
                  className="text-sm text-red-700"
                >
                  {blockerSentence(blocker)}
                  {/* A stale summary is operational repair, not a wait, so
                      the line carries the place it is repaired from (F27). */}
                  {blockerCode(blocker) === 'projection_stale' && (
                    <>
                      {' '}
                      <Link
                        to={`/equipment-inventory?device_id=${blocker.device_id ?? device.id}`}
                        title={TOOLTIPS.openLink(EQUIPMENT_INVENTORY)}
                        className="text-link hover:underline"
                      >
                        Open the Equipment Inventory
                      </Link>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* §3 — one line for what the API would not show at all. */}
          {hiddenBlockers > 0 && (
            <p data-testid="hidden-blockers" className="mt-2 text-sm text-gray-600">
              {hiddenBlockers === 1
                ? '1 more reason not to send belongs to a Clip you cannot see.'
                : `${hiddenBlockers} more reasons not to send belong to Clips you cannot see.`}
            </p>
          )}
        </section>
      )}

      {/* 3 — Draft -------------------------------------------------------- */}
      {canContact && (
        <section data-testid="missing-draft" className="border-t pt-4">
          <div className="flex items-baseline justify-between gap-3">
            <h4 className="font-medium">Draft</h4>
            {edited && draft && (
              <Button
                variant="secondary"
                size="sm"
                title={TOOLTIPS.resetDraft}
                onClick={resetDraft}
              >
                Reset to draft
              </Button>
            )}
          </div>

          {selected.length === 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              Tick a part above and a message to the homeowner is drafted here.
            </p>
          )}
          {draftBusy && <p className="mt-2 text-sm text-muted-foreground">Writing the draft…</p>}
          {/* R5 — the draft failed, so it is said where the draft would have
              been, with the one action that makes sense here: ask for it
              again. Send is already off (there is no draft to send) and says
              nothing more about it. */}
          {draftError && (
            <div data-testid="draft-error" className="mt-2">
              <p className="text-sm text-red-700" role="alert">
                {draftError}
              </p>
              <Button
                variant="secondary"
                size="sm"
                data-testid="draft-retry"
                title={TOOLTIPS.retryDraft}
                onClick={retryDraft}
                className="mt-2"
              >
                Try again
              </Button>
            </div>
          )}

          {draft && (
            <>
              <dl className="mt-2 text-xs text-muted-foreground">
                <div data-testid="draft-from">
                  <dt className="inline">From: </dt>
                  <dd className="inline font-mono">{draft.from_address}</dd>
                </div>
                <div data-testid="draft-reply-to">
                  <dt className="inline">Reply to: </dt>
                  <dd className="inline font-mono">{draft.reply_to}</dd>
                </div>
              </dl>

              <div className="mt-3">
                <label
                  htmlFor="missing-subject"
                  className="text-xs uppercase tracking-wide text-muted-foreground"
                >
                  Subject
                </label>
                <Input
                  id="missing-subject"
                  type="text"
                  title={TOOLTIPS.subject}
                  aria-describedby={`missing-subject-help missing-subject-count${subjectError ? ' missing-subject-error' : ''}`}
                  aria-invalid={Boolean(subjectError)}
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    setEdited(true);
                  }}
                  className="mt-1"
                />
                <span id="missing-subject-help" className="mt-1 block text-xs text-muted-foreground">{TOOLTIPS.subject}</span>
                <span id="missing-subject-count" data-testid="subject-count" className="mt-1 block text-xs text-muted-foreground">
                  {subject.length} / {SUBJECT_MAX}
                </span>
                {subjectError && (
                  <span
                    id="missing-subject-error"
                    data-testid="subject-error"
                    role="alert"
                    className="mt-1 block text-xs text-red-700"
                  >
                    {subjectError}
                  </span>
                )}
              </div>

              <div className="mt-3">
                <label
                  htmlFor="missing-body"
                  className="text-xs uppercase tracking-wide text-muted-foreground"
                >
                  Message
                </label>
                <Textarea
                  id="missing-body"
                  rows={10}
                  title={TOOLTIPS.body}
                  aria-describedby={`missing-body-help missing-body-count${bodyError ? ' missing-body-error' : ''}`}
                  aria-invalid={Boolean(bodyError)}
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setEdited(true);
                  }}
                  className="mt-1 font-mono"
                />
                <span id="missing-body-help" className="mt-1 block text-xs text-muted-foreground">{TOOLTIPS.body}</span>
                <span id="missing-body-count" data-testid="body-count" className="mt-1 block text-xs text-muted-foreground">
                  {body.length} / {BODY_MAX.toLocaleString()}
                </span>
                {bodyError && (
                  <span
                    id="missing-body-error"
                    data-testid="body-error"
                    role="alert"
                    className="mt-1 block text-xs text-red-700"
                  >
                    {bodyError}
                  </span>
                )}
              </div>

              <div
                data-testid="draft-footer"
                className="mt-3 rounded-sm border border-border bg-muted p-3 text-xs text-muted-foreground whitespace-pre-wrap"
              >
                {draft.footer_text}
              </div>
              <p data-testid="draft-fixed-note" className="mt-1 text-xs text-muted-foreground">
                FluxHive adds the footer, the unsubscribe link and a &quot;Reply with photos&quot;
                line, and sends the message in the FluxHive layout with example photos under
                each numbered item whose heading (such as &quot;Filter edge:&quot;) is left as
                written. None of that can be edited here.
              </p>
            </>
          )}
        </section>
      )}

      {/* 4 — Send --------------------------------------------------------- */}
      {canContact && (
        <section data-testid="missing-send" className="border-t pt-4">
          <h4 className="font-medium">Send</h4>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              title={sendTitle()}
              disabled={!canSend || sending}
              aria-describedby={!canSend || sending ? (blockers.length > 0 ? 'missing-send-blocker' : 'missing-send-reason') : undefined}
              onClick={sendNow}
            >
              {sending ? 'Sending…' : 'Send request'}
            </Button>
            {(!canSend || sending) && blockers.length === 0 && (
              <span id="missing-send-reason" className="text-xs text-muted-foreground">
                {sendTitle()}
              </span>
            )}
            {outcome && (
              <span data-testid="send-outcome" className="text-sm text-green-800">
                {outcome}
              </span>
            )}
          </div>
          {refusal && (
            <p data-testid="send-refusal" className="mt-2 text-sm text-red-700" role="alert">
              {refusal.message}{' '}
              <span className="font-mono text-xs text-muted-foreground">({refusal.code})</span>
            </p>
          )}

          {/* R3 F25 — a send nobody can account for. Reading is the only
              thing on offer; Send stays off until this is settled.
              R4 F30 — one sentence, and it says what the LAST read of this
              request established: the polling loop wrote the first one, and
              every Check status replaces it. */}
          {unresolved && (
            <div data-testid="send-unresolved" className="mt-2 rounded-sm border border-status-warning-border bg-amber-50 p-3">
              <p
                data-testid={checkNote === null ? 'recovery-state' : 'check-note'}
                className="text-sm text-amber-800"
                role="status"
              >
                {checkNote ?? own(RECOVERY_TEXT, recovery)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  title={TOOLTIPS.checkStatus}
                  disabled={checking}
                  onClick={checkStatus}
                >
                  {checking ? 'Checking…' : 'Check status'}
                </Button>
                <Button
                  variant="secondary"
                  title={TOOLTIPS.cancelRecovery}
                  disabled={checking}
                  onClick={cancelUnresolved}
                >
                  Cancel
                </Button>
                {/* R11 — Send is NOT off any more, so the panel says what
                    pressing it again would risk instead of pretending the
                    decision has been taken for the operator. */}
                <span className="text-xs text-gray-600">
                  Check status reads this request back. It never sends anything. Sending again
                  may send twice.
                </span>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 5 — History ------------------------------------------------------ */}
      <section data-testid="missing-history" className="border-t pt-4">
        {/* F27: the table holds every attempt, including the ones that never
            reached the mail service. "Sent" was true of some of them. */}
        <h4 className="font-medium">Photo requests attempted for this system</h4>
        {drawerNote && (
          <p data-testid="drawer-note" className="mt-2 text-sm text-gray-700">
            {drawerNote}
          </p>
        )}
        {history.length === 0 && (
          <p className="mt-2 text-sm text-muted-foreground">
            Nobody has been asked about this system yet.
          </p>
        )}
        <ul className="mt-2 space-y-4">
          {history.map((row) => (
            <li key={row.request_id} data-testid="history-row" className="rounded-sm border p-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                <span data-testid="history-when" className="font-medium">
                  {easternDateTime(row.requested_at, 'date not recorded')}
                </span>
                <span data-testid="history-asker" className="text-muted-foreground">
                  asked by {askerOf(row)}
                </span>
                <span>
                  {row.components.map(componentLabel).join(', ')}
                </span>
              </div>
              {/* F27 — a row is an ATTEMPT until a send state says otherwise.
                  Refused, gated and never-dispatched rows put no message in
                  front of a homeowner, so none of them is described as one. */}
              <p data-testid="history-outcome" className="mt-1 text-sm text-gray-700">
                {(() => {
                  const attemptedOn = `Attempt on ${easternDateTime(row.requested_at, 'an unrecorded date')}: not sent.`;
                  if (row.status === 'refused' || row.refusal) {
                    return `${attemptedOn} ${refusalSentence(row.refusal, null)}`;
                  }
                  if (!row.send) return attemptedOn;
                  if (row.send.state === 'sending') {
                    return `Attempt on ${easternDateTime(row.requested_at, 'an unrecorded date')}: still being sent.`;
                  }
                  if (!SENT_STATES.has(row.send.state)) {
                    return `${attemptedOn} ${sendStateText(row.send.state)}.`;
                  }
                  return [
                    `${sendStateText(row.send.state)} ${easternDateTime(row.send.sent_at ?? row.send.sending_at)}`,
                    ...row.events.map((event) => `${eventText(event.type)} ${easternDateTime(event.at)}`),
                  ].join(' · ');
                })()}
              </p>
              {row.send?.send_error && (
                <p className="mt-1 text-xs text-red-700">{row.send.send_error}</p>
              )}

              <ul className="mt-2 space-y-2">
                {row.targets.map((target) => {
                  const key = targetKey(row, target);
                  const phrase = rowPhrase(
                    target.component,
                    target.component === 'filter' ? target.device_id : null,
                  );
                  return (
                    <li key={key} data-testid="history-target" className="text-sm">
                      <span className="font-medium">
                        {rowLabel(
                          target.component,
                          target.component === 'filter' ? target.device_id : null,
                        )}
                      </span>
                      <span className="ml-2 text-muted-foreground">
                        {target.reply_outcome
                          ? `${own(REPLY_LABEL, target.reply_outcome) ?? target.reply_outcome} on ${easternDate(target.replied_at, 'an unrecorded date')}`
                          : 'No reply yet'}
                      </span>
                      {canContact && !target.reply_outcome && (
                        <span className="ml-2 inline-flex flex-wrap gap-2">
                          {REPLY_BUTTONS.map(({ value, label, describe, title }) => (
                            <Button
                              variant="secondary"
                              size="sm"
                              key={value}
                              aria-label={describe(phrase)}
                              title={title(rowPart(
                                target.component,
                                target.component === 'filter' ? target.device_id : null,
                              ))}
                              disabled={replyBusy === key}
                              onClick={() => reply(row, target, value)}
                            >
                              {label}
                            </Button>
                          ))}
                        </span>
                      )}
                      {/* R5 — a reply that was not recorded is said on the
                          target it was pressed for, not at the top of the
                          list where it names none of them. */}
                      {replyError?.key === key && (
                        <span
                          data-testid="reply-error"
                          role="alert"
                          className="mt-1 block text-sm text-red-700"
                        >
                          {replyError.message}
                        </span>
                      )}
                      {absenceOffers.includes(key) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          data-testid="absence-edit"
                          title={TOOLTIPS.absenceOffer}
                          onClick={() => openDrawer({
                            device: clipDevice(target.device_id)
                              ?? { id: device.id, name: device.name },
                            ...ABSENCE_EDIT,
                          })}
                          className="ml-2"
                        >
                          Record that this system has no {componentPhrase(target.component)}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </section>

      {drawer && (
        <EditEquipmentDrawer
          device={drawer.device}
          initialTab={drawer.tab}
          focusField={drawer.field}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            load();
            refetchDevice?.();
          }}
          projectionStamp={projectionStamp}
          onWritten={followWrite}
        />
      )}
    </div>
  );
};

export default MissingTab;
